import { Timeline } from "@xzdarcy/react-timeline-editor";
import lodash from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TriggerMonitor from "./trigger/trigger_monitor";
import {
  draftDialogAdvanceUndo,
  draftDialogAudioUrlSelector,
  draftDialogEditorDataSelector,
  draftDialogPlaySelector,
  draftDialogSecondSelector,
} from "store/bailiff/draft/dialog/draft_dialog.selector";
import TriggerPlay from "./trigger/trigger_play";
import ColorBlock from "./component/color_block";
import {
  CloseOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  addTimelineAction,
  addTimelineNewSpeaker,
  setDraftDialogAdvanceUndo,
  setDraftDialogPlay,
  undoTimelineAction,
} from "store/bailiff/draft/dialog/draft_dialog.action";
import DialogLabelEdit from "./dialog/dialog_label_edit";
import TriggerSpace from "./trigger/trigger_space";
import {
  Button,
  Divider,
  InputNumber,
  Popover,
  Tooltip,
  Typography,
} from "antd";
import {
  useActionDialogAdvance,
  useActionDialogAdvanceSave,
  useActionDialogSpeakerColor,
  useActionDialogSpeakerName,
  useActionDialogSplitAction,
} from "hook/draft/dialog/useActionDialogAdvance";
import DialogChangeColor from "screen/03_Bailiff/03_Segment/dialog/dialog_change_color";
import DialogChangeName from "screen/03_Bailiff/03_Segment/dialog/dialog_change_name";
import { TfiSplitH } from "react-icons/tfi";
import { LiaUndoAltSolid } from "react-icons/lia";
import { refineTimer } from "util/refine_timer.function";
import DialogAdvanceSave from "screen/03_Bailiff/03_Segment/dialog/dialog_advance_save";
//
//component
//redux
//selector
//actions
//utils
//hook
//str
import "./timeline_editor.css";

// 1. Định nghĩa dữ liệu các hàng (Rows) và hành động (Actions)
const defaultData = [
  {
    id: "0", // ID của hàng
    actions: [
      {
        id: "action1",
        start: 0, // Bắt đầu tại giây thứ 0
        end: 2, // Kết thúc tại giây thứ 2
        content: "Tôi là Robert",
        effectId: "effect1", // Dùng style của effect1
      },
    ],
  },
  {
    id: "1",
    actions: [
      {
        id: "action2",
        start: 2.5,
        end: 5,
        content: "Tôi là Robert",
        effectId: "effect2", // Dùng style của effect2
      },
    ],
  },
];

const TimelineEditor = () => {
  // -------------------------- VAR -----------------------------
  const { handleConfirm } = useActionDialogAdvance();
  const { handleOpen } = useActionDialogSpeakerColor();
  const { handleOpen: handleOpenName } = useActionDialogSpeakerName();
  const { handleSplit } = useActionDialogSplitAction();
  const { handleOpen: handleOpenSave } = useActionDialogAdvanceSave();
  const { handleConfirmCloseAdvance } = useActionDialogAdvance();
  const timeRef = useRef(null);
  // -------------------------- STATE ---------------------------
  const [scale, setScale] = useState(20);
  const [audio, setAudio] = useState(null);
  const [editor, setEditor] = useState(defaultData);

  // -------------------------- REDUX ---------------------------
  const dispatch = useDispatch();
  const editorData = useSelector(draftDialogEditorDataSelector);
  const url = useSelector(draftDialogAudioUrlSelector);
  const play = useSelector(draftDialogPlaySelector);
  const second = useSelector(draftDialogSecondSelector);
  const undo = useSelector(draftDialogAdvanceUndo);
  const icon = !play ? <PlayCircleOutlined /> : <PauseCircleOutlined />;
  const title = play
    ? "Tạm dừng Audio (Phím tắt Space)"
    : "Nghe Audio (Phím tắt Space)";
  const duration = refineTimer({ timer: Math.round(second) });
  // -------------------------- FUNCTION ------------------------
  const handleAddBlock = useCallback(
    ({ id }) => {
      const current = audio?.currentTime;
      dispatch(addTimelineAction({ origin: editor, id, current }));
    },
    [audio?.currentTime, dispatch, editor],
  );

  // thêm s mới.
  const handleAddNewSpeaker = () => {
    dispatch(addTimelineNewSpeaker({ origin: editor }));
  };

  const togglePLay = useCallback(() => {
    dispatch(setDraftDialogPlay(!play));
  }, [dispatch, play]);

  // handle cancel.
  const handleCancel = () => {
    handleConfirmCloseAdvance();
  };

  // handle undo
  const handleUndo = () => {
    if (!undo) return;
    dispatch(undoTimelineAction({ undo }));
  };

  // -------------------------- EFFECT --------------------------
  // trigger url.
  useEffect(() => {
    // If there's no URL, do nothing and ensure audio is null.
    if (!url) {
      setAudio(null);
      return;
    }

    const audioInstance = new Audio(url);
    setAudio(audioInstance);

    // This is the cleanup function.
    // It runs when the component unmounts or before the effect runs again.
    return () => {
      // Pause the audio, remove its source to release resources.
      audioInstance.pause();
      audioInstance.removeAttribute("src");
      audioInstance.load();
    };
  }, [url]);

  useEffect(() => {
    if (editorData) {
      const clone = lodash.cloneDeep(editorData);
      setEditor(clone);
    }
  }, [editorData]);

  // split
  const handleSplitAction = () => {
    dispatch(setDraftDialogAdvanceUndo(editor));
    handleSplit(editor);
  };
  // -------------------------- DATA FUNCTION -------------------
  // -------------------------- RENDER --------------------------
  const renderHeader = () => {
    return (
      <div className="flex h-[40px] w-full flex-row items-center justify-between">
        {renderSetting()}
        <div className="flex flex-row gap-1">
          {renderSave()}
          {renderClose()}
        </div>
      </div>
    );
  };

  const renderSetting = () => {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="min-w-[80px]">{duration}</span>
        {renderPlayAudio()}
        {renderScaleNumber()}
        {renderClip()}
        {renderUndo()}
      </div>
    );
  };

  const renderSave = () => {
    return (
      <Tooltip title={"Lưu đoạn văn"}>
        <Button
          onClick={() => handleOpenSave(editor)}
          type="text"
          size="large"
          icon={<SaveOutlined />}
        />
      </Tooltip>
    );
  };

  const renderPlayAudio = () => {
    return (
      <Tooltip title={title}>
        <Button onClick={togglePLay} type="text" size="large" icon={icon} />
      </Tooltip>
    );
  };

  const renderScaleNumber = () => {
    return (
      <div className="flex flex-row items-center gap-1">
        <span>Scale</span>
        <InputNumber
          className="max-w-[40px]"
          size="small"
          value={scale}
          onChange={(number) => setScale(number)}
          step={1}
          min={1}
          max={20}
        />
      </div>
    );
  };

  const renderClip = () => {
    return (
      <Tooltip title="Cắt đoạn văn">
        <Button
          onClick={handleSplitAction}
          size="large"
          type="text"
          icon={<TfiSplitH />}
        />
      </Tooltip>
    );
  };

  const renderUndo = () => {
    return (
      <Tooltip title="Hoàn Tác">
        <Button
          onClick={handleUndo}
          size="large"
          disabled={!undo}
          // className="text-white hover:!text-gray-300 disabled:!text-gray-500"
          type="text"
          icon={<LiaUndoAltSolid />}
        />
      </Tooltip>
    );
  };

  const renderClose = () => {
    return (
      <Button
        type="text"
        size="large"
        // className="text-white hover:!text-gray-300"
        icon={<CloseOutlined />}
        onClick={handleCancel}
      />
    );
  };

  const renderSide = () => {
    return (
      <div
        style={{ overflow: "overlay", width: 100, height: 180 }}
        className="mt-[38px] flex flex-col gap-[2px] pb-2"
      >
        {editor?.map((item, index) => {
          const bgcolor = item?.bgColor || "#f0f0f0";
          const color = item?.color || "black";
          return (
            <Popover
              placement="rightTop"
              content={renderContent({ id: item?.id })}
              trigger={"contextMenu"}
              styles={{ body: { padding: 0 } }}
            >
              <Tooltip title={`Speaker: ${item?.name || item?.id}`}>
                <div
                  className="flex min-h-[35px] cursor-pointer flex-row items-center justify-between rounded-sm px-3 py-1"
                  style={{ backgroundColor: bgcolor, color }}
                  key={item?.id}
                  onClick={() => handleAddBlock({ id: item?.id })}
                >
                  <Typography.Text
                    style={{ color }}
                    ellipsis={{ rows: 1 }}
                    className="text-[12px]"
                  >
                    {`Thêm ${item?.name || item?.id}`}{" "}
                  </Typography.Text>
                  <PlusOutlined />
                </div>
              </Tooltip>
            </Popover>
          );
        })}
        <div
          className="mt-1 flex min-h-[30px] cursor-pointer flex-row items-center justify-between rounded-sm border-[1px] border-dashed border-gray-500 px-3 py-1 text-black hover:bg-gray-200"
          key={"plus"}
          onClick={handleAddNewSpeaker}
        >
          <div className="text-[12px]"> {`Thêm S`} </div>
          <PlusOutlined />
        </div>
      </div>
    );
  };

  const renderContent = ({ id }) => {
    return (
      <div className="min-h-[30px] min-w-[100px] rounded-sm bg-white p-[5px]">
        <Button
          onClick={() => handleOpenName(editor, id)}
          className="!rounded-sm"
          type="text"
          block
        >
          Sửa tên
        </Button>
        <Divider className="bg-gray-100" style={{ margin: 0 }} />
        <Button
          onClick={() => handleOpen(editor, id)}
          className="!rounded-sm"
          type="text"
          block
        >
          Đổi màu
        </Button>
        <Divider className="bg-gray-100" style={{ margin: 0 }} />
        <Button
          onClick={() => handleConfirm({ origin: editor, id })}
          className="!rounded-sm"
          type="text"
          block
          danger
        >
          Xoá
        </Button>
      </div>
    );
  };

  // -------------------------- MAIN ----------------------------
  if (!audio) return null;
  return (
    <>
      <DialogLabelEdit />
      <DialogChangeColor />
      <DialogChangeName />
      <DialogAdvanceSave />
      <TriggerPlay audio={audio} />
      <TriggerMonitor timeRef={timeRef} audio={audio} />
      <TriggerSpace />
      <>
        {renderHeader()}
        <Divider className="bg-gray-200" style={{ margin: 5 }} />
        <div className="flex flex-row gap-1">
          {renderSide()}
          <Timeline
            // className="timeline-editor-light"
            ref={timeRef}
            editorData={editor || []} // Dữ liệu timeline
            rowHeight={35}
            autoScroll
            scale={scale}
            dragLine
            style={{ height: 200, width: "100%" }}
            onChange={(newData) => {
              // Hàm chạy khi bạn kéo thả
              console.log({ newData });
              setEditor(newData);
            }}
            getActionRender={(action, row) => {
              const actionId = action?.id;
              const id = row?.id;
              return (
                <ColorBlock
                  action={action}
                  actionId={actionId}
                  id={id}
                  editor={editor}
                />
              );
            }}
            getRowId={(row) => row.id} // Cách lấy ID của hàng
            onClickTimeArea={(time) => {
              if (!audio) return;
              audio.currentTime = time;
              return true; // Trả về true để cho phép di chuyển con trỏ
            }}
          />{" "}
        </div>
      </>
    </>
  );
};
export default TimelineEditor;
